import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ApiCall } from 'components/common/services/ApiServices';
import CustomNotify from 'components/common/atoms/CustomNotify';
import { t } from '../translation/Translation';
import { selectAuth } from 'features/auth/AuthSlice';
import {
    CLARITALK_START_RECORDING,
    STOP_RECORDING,
    PHONE_CALL_URL
} from 'routes/ApiEndpoints';
import {
    CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
    SUCCESS,
    ERROR
} from 'Constants';
import PhoneCallIcon from 'static/images/PhoneCall';
import StartIcon from 'static/images/StartIcon';
import StopIcon from 'static/images/StopIcon';
import SelectWithSearch from 'components/common/atoms/SelectWithSearch';
import secureLocalStorage from 'react-secure-storage';
import SuccessionPopup from '../taskDeterminer/SuccessionPopup';
import { EntityData } from 'components/common/atoms/RightSideBar';
import { formatPhoneFor3CX } from 'components/common/utlis/HelperFunctions';
import './claritalk.css';

export interface MeetingType {
    value: number | string;
    label: string;
}

export interface ClaritalkCallIconProps {
    meetingTypes?: MeetingType[];
    entityId: number | null | undefined;
    entityType: 'candidate' | 'company' | 'company-contacts' | number | null;
    entityName: string;
    phone?: string | null;
    userId?: number | null;
    companyId?: number | null;
    brightId?: number | null;
    isRecording?: boolean;
    onRecordingStart?: () => void;
    onRecordingStop?: () => void;
    showPhoneIcon?: boolean;
    showAsText?: boolean;
    navigateTo3CX?: boolean;
}

const ClaritalkCallIcon: React.FC<ClaritalkCallIconProps> = ({
    meetingTypes = [],
    entityId,
    entityType,
    entityName,
    phone,
    userId,
    companyId,
    brightId,
    isRecording = false,
    onRecordingStart,
    onRecordingStop,
    showPhoneIcon = true,
    showAsText = false,
    navigateTo3CX = false
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedMeetingType, setSelectedMeetingType] = useState<MeetingType | null>(null);

    const authUser = useSelector(selectAuth);

    // Use meeting types from props or fall back to auth state
    const claritalkMeetingTypes = React.useMemo(() => {
        if (meetingTypes && meetingTypes.length > 0) {
            return meetingTypes;
        }
        return authUser.claritalkMeetingTypes ?? [];
    }, [meetingTypes, authUser.claritalkMeetingTypes]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Follow-up popup state
    const initialEntityData: EntityData = {
        id: null,
        office: null,
        type: null,
        typeId: null
    };
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [entity, setEntity] = useState<EntityData>(initialEntityData);

    const handleFollowUpModalClose = () => {
        setShowFollowUpModal(false);
        setEntity(initialEntityData);
    };

    const handleFollowUp = () => {
        const resolvedType = entityType === 'candidate' ? 'candidate'
            : entityType === 'company' ? 'company'
            : entityType === 'company-contacts' ? 'company'
            : entityType === 1 ? 'candidate'
            : entityType === 2 ? 'company'
            : entityType === 3 ? 'company'
            : '';
        const resolvedTaskType = (resolvedType === 'candidate') ? 3
            : (resolvedType === 'company') ? 2
            : null;

        setEntity({
            id: null,
            office: null,
            type: resolvedType,
            typeId: entityId ?? null,
            taskType: resolvedTaskType,
        });
        setShowFollowUpModal(true);
    };

    // Get language from localStorage
    const getLanguageKey = (): string => {
        let langKey = secureLocalStorage.getItem("site_lang") as string ?? "en";
        if (langKey === 'en-source') {
            langKey = 'en';
        }
        return langKey;
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle icon click - open meeting type dropdown (play button / recording mode)
    const handleIconClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
    };

    // Handle call icon / phone number click - go directly to 3CX + follow-up popup.
    // No meeting type dropdown and no Claritalk recording here.
    const handleQuickCall = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (phone) {
            openThreeCX();
        }
        if (brightId) {
            handleFollowUp();
        }
    };

    // Handle meeting type selection from dropdown
    const handleMeetingTypeChange = (selected: MeetingType | null) => {
        setSelectedMeetingType(selected);
        if (selected && selected.value) {
            handleStartRecording(selected.value);
        }
    };

    // Navigate to 3CX (with the national trunk "0" removed from the number)
    const openThreeCX = () => {
        if (phone) {
            const formattedPhone = formatPhoneFor3CX(phone);
            const url = `${PHONE_CALL_URL}${formattedPhone.replace('+', '%2B')}`;
            window.open(url, '_blank');
        }
    };

    // Handle start recording
    const handleStartRecording = async (meetingTypeId: number | string) => {
        setIsDropdownOpen(false);
        setLoading(true);

        try {
            // Navigate to 3CX if triggered from call icon or phone number
            if (navigateTo3CX && phone) {
                openThreeCX();
            }

            // Open follow-up popup if triggered from call icon or phone number
            if (navigateTo3CX && brightId) {
                handleFollowUp();
            }

            const languageKey = getLanguageKey();

            const data = {
                type: meetingTypeId,
                user: entityName,
                id: userId,
                companyId: companyId ?? null,
                title: '',
                languageKey: languageKey,
                entityId: entityId,
                entityType: entityType === 'candidate' ? 1 : entityType === 'company' ? 2 : entityType === 'company-contacts' ? 3 : entityType,
                isOnlineMeeting: true,
                userId: authUser.userId
            };

            const response = await ApiCall.service(
                CLARITALK_START_RECORDING,
                'POST',
                data,
                true,
                CENTRAL_DATA_MANAGEMENT_MICROSERVICE
            );

            if (response?.status === 200) {
                CustomNotify({ type: SUCCESS, message: t("Recording started successfully") });
                onRecordingStart?.();
            } else if (response?.status === 503) {
                CustomNotify({ type: ERROR, message: t(response?.error) });
            } else {
                CustomNotify({
                    type: ERROR,
                    message: t(response?.error || "Recording is temporarily unavailable. Please contact the administrator for assistance.")
                });
            }
        } catch (error) {
            console.error('Failed to start recording:', error);
            CustomNotify({
                type: ERROR,
                message: t("Failed to start recording")
            });
        } finally {
            setLoading(false);
            setSelectedMeetingType(null);
        }
    };

    // Handle stop recording
    const handleStopRecording = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setLoading(true);
        try {
            const response = await ApiCall.service(
                STOP_RECORDING,
                'POST',
                { agentId: authUser.userId },
                true,
                CENTRAL_DATA_MANAGEMENT_MICROSERVICE
            );

            if (response?.status === 200) {
                CustomNotify({ type: SUCCESS, message: t("Recording stopped successfully") });
                onRecordingStop?.();
            } else {
                CustomNotify({
                    type: ERROR,
                    message: t(response?.error || "Failed to stop recording")
                });
            }
        } catch (error) {
            console.error('Failed to stop recording:', error);
            CustomNotify({
                type: ERROR,
                message: t("An error occurred while stopping the recording")
            });
        } finally {
            setLoading(false);
        }
    };

    // Meeting type dropdown
    const renderDropdown = () => {
        if (!isDropdownOpen || claritalkMeetingTypes.length === 0) return null;

        return (
            <div className="claritalk-call-dropdown">
                <div className="claritalk-call-dropdown-header">
                    <span>{t("Select meeting type")}</span>
                </div>
                {claritalkMeetingTypes.length > 8 ? (
                    <div className="claritalk-call-dropdown-content">
                        <SelectWithSearch
                            search={true}
                            options={claritalkMeetingTypes}
                            value={selectedMeetingType}
                            onChange={handleMeetingTypeChange}
                            name="meetingType"
                            placeHolder={t("Search and select...")}
                            isSelectOption={false}
                            isTranslate={true}
                            menuIsOpen={true}
                            disablePortal={true}
                        />
                    </div>
                ) : (
                    <div className="claritalk-call-dropdown-items">
                        {claritalkMeetingTypes.map((meeting: MeetingType) => (
                            <div
                                key={meeting.value}
                                className="claritalk-call-dropdown-item"
                                onClick={() => handleStartRecording(meeting.value)}
                            >
                                {t(meeting.label)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Mode 1: Phone/Call - goes directly to 3CX + follow-up popup (no Claritalk)
    // showAsText=true → displays phone number as clickable text
    // showAsText=false → displays PhoneCallIcon
    if (showPhoneIcon) {
        return (
            <div className="claritalk-call-icon-container d-inline-block" ref={dropdownRef}>
                {showAsText ? (
                    <a
                        className="cursor-pointer"
                        onClick={handleQuickCall}
                        title={phone ? `${t("Call")} ${phone}` : t("Call")}
                    >
                        {phone}
                    </a>
                ) : (
                    <span
                        className="cursor-pointer claritalk-icon-wrapper"
                        onClick={handleQuickCall}
                        title={phone ? `${t("Call")} ${phone}` : t("Call")}
                    >
                        <PhoneCallIcon />
                    </span>
                )}

                {showFollowUpModal && (
                    <SuccessionPopup
                        entity={entity}
                        show={showFollowUpModal}
                        onHide={handleFollowUpModalClose}
                        refresh={handleFollowUpModalClose}
                    />
                )}
            </div>
        );
    }

    // Mode 2: Start/Stop recording icon - toggles between StartIcon and StopIcon
    return (
        <div className="claritalk-call-icon-container" ref={dropdownRef}>
            {isRecording ? (
                <span
                    className="cursor-pointer claritalk-icon-wrapper"
                    onClick={handleStopRecording}
                    title={t("Stop recording")}
                >
                    {loading ? (
                        <i className="pi pi-spin pi-spinner"></i>
                    ) : (
                        <StopIcon />
                    )}
                </span>
            ) : (
                <span
                    className="cursor-pointer claritalk-icon-wrapper"
                    onClick={handleIconClick}
                    title={t("Start recording")}
                >
                    {loading ? (
                        <i className="pi pi-spin pi-spinner"></i>
                    ) : (
                        <StartIcon />
                    )}
                </span>
            )}

            {renderDropdown()}

            {showFollowUpModal && (
                <SuccessionPopup
                    entity={entity}
                    show={showFollowUpModal}
                    onHide={handleFollowUpModalClose}
                    refresh={handleFollowUpModalClose}
                />
            )}
        </div>
    );
};

export default ClaritalkCallIcon;
